import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { MASCOT } from "@/lib/assets";
import {
  ArrowRight, CheckCircle, ShieldCheck, WhatsappLogo, Target, Heart, Sparkle,
  GraduationCap, BookOpen, Robot, Trophy, Cardholder, WifiSlash, Star,
} from "@phosphor-icons/react";

const FOUNDER_PHOTO = "/founder.jpg";

const VALUES = [
  { icon: GraduationCap, tone: "#FF4B0A", title: "Accessibilité pour tous", desc: "Une éducation de qualité au prix d'un repas, accessible même en connexion lente et hors-ligne, partout au Cameroun." },
  { icon: Target, tone: "#05357E", title: "Excellence académique", desc: "Des contenus 100% conformes aux programmes officiels du MINESEC et du MINEDUB, conçus avec des enseignants camerounais." },
  { icon: Heart, tone: "#EC4899", title: "Bienveillance", desc: "On motive, on encourage, on explique — jamais on ne juge. Chaque élève avance à son rythme, en confiance." },
  { icon: Sparkle, tone: "#FFB800", title: "Innovation utile", desc: "L'intelligence artificielle au service de la pédagogie : un coach personnel dans la poche de chaque candidat." },
];

const OFFER = [
  { icon: BookOpen, title: "Cours & fiches", desc: "Un programme complet, chapitre par chapitre, pour toutes les classes d'examen." },
  { icon: Robot, title: "Coach IA 24h/24", desc: "Un professeur qui explique, interroge et corrige à toute heure." },
  { icon: GraduationCap, title: "Examens blancs", desc: "Des sujets inédits, chronométrés et corrigés pour s'entraîner comme le jour J." },
  { icon: Trophy, title: "Motivation & jeu", desc: "XP, badges, séries et classement national pour garder l'envie de réviser." },
  { icon: Cardholder, title: "Carnet d'erreurs", desc: "Un système de répétition espacée qui transforme chaque erreur en progrès." },
  { icon: WifiSlash, title: "Mode hors-ligne", desc: "Réviser sans coupure, même quand la connexion faiblit." },
];

const STATS = [
  ["50 000+", "élèves accompagnés"],
  ["4,8/5", "note des familles"],
  ["15 000+", "avis positifs"],
  ["100%", "programmes officiels"],
];

function PublicHeader() {
  return (
    <>
      <div className="bg-forest px-4 py-2 text-center text-xs font-medium text-white sm:text-sm">
        <CheckCircle weight="fill" className="mr-1.5 inline text-gold" size={15} />
        100% conforme aux programmes officiels du Cameroun — MINESEC & MINEDUB
      </div>
      <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" data-testid="about-logo"><Logo /></Link>
          <nav className="hidden items-center gap-8 text-sm font-semibold text-ink/70 md:flex">
            <Link to="/" className="hover:text-terracotta">Accueil</Link>
            <a href="/#matieres" className="hover:text-terracotta">Matières</a>
            <a href="/#fondateur" className="hover:text-terracotta">Fondateur</a>
            <a href="/#tarifs" className="hover:text-terracotta">Tarifs</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/connexion" className="hidden rounded-full px-4 py-2 text-sm font-semibold text-ink transition hover:bg-sand sm:inline-block">Connexion</Link>
            <Link to="/connexion?mode=register" className="rounded-full bg-terracotta px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_22px_rgba(255,75,10,0.35)] transition hover:-translate-y-0.5 hover:bg-[#E03E00]">Commencer</Link>
          </div>
        </div>
      </header>
    </>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-border bg-white py-10">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:px-6 md:flex-row lg:px-8">
        <Logo />
        <p className="text-sm text-slate-500">© 2026 YAYA EXAM ACADEMY — Réussir aujourd'hui. Construire demain.</p>
        <div className="flex items-center gap-4 text-sm text-slate-500">
          <Link to="/a-propos" className="hover:text-terracotta">À propos</Link>
          <Link to="/contact" className="hover:text-terracotta">Contact</Link>
          <a href="https://wa.me/237659946824" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#25D366]"><WhatsappLogo weight="fill" /> Support</a>
        </div>
      </div>
    </footer>
  );
}

export default function About() {
  const [founderOk, setFounderOk] = useState(true);
  return (
    <div className="min-h-screen bg-white text-ink">
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-grid opacity-70" />
        <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-terracotta/10 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-20">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-terracotta/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-terracotta">
              <Sparkle weight="fill" size={14} /> À propos du groupe YAYA
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
              Démocratiser la <span className="text-terracotta">réussite</span> scolaire au Cameroun
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg">
              Le groupe YAYA met la technologie et la pédagogie au service d'une seule idée : offrir à chaque élève,
              où qu'il vive, les moyens de réussir ses examens officiels et de construire son avenir.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/connexion?mode=register" data-testid="about-cta" className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-bold text-white shadow-[0_12px_30px_rgba(255,75,10,0.4)] transition hover:-translate-y-0.5 hover:bg-[#E03E00]">
                Rejoindre YAYA <ArrowRight weight="bold" className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a href="/#fondateur" className="inline-flex items-center gap-2 rounded-full border-2 border-forest px-7 py-3 text-sm font-bold text-forest transition hover:bg-forest hover:text-white">
                Le fondateur
              </a>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "0.1s" }}>
            <div className="absolute -inset-6 rounded-full bg-forest/5 blur-2xl" />
            <img src={MASCOT.book} alt="Mascotte YAYA" className="relative mx-auto w-64 drop-shadow-xl sm:w-72" />
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-cream py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-terracotta">Notre mission</span>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
            « Réussir aujourd'hui. Construire demain. »
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-slate-600">
            Au Cameroun, la réussite aux examens dépend trop souvent des moyens de la famille ou du lieu de naissance.
            Le groupe YAYA existe pour briser cette inégalité : mettre entre les mains de chaque candidat — du BEPC au
            Baccalauréat, du technique au sous-système anglophone — un accompagnement de qualité, motivant et abordable,
            disponible à tout moment, même hors connexion.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-terracotta">Nos valeurs</span>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Ce qui nous guide au quotidien</h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="rounded-3xl border border-border bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="grid h-14 w-14 place-items-center rounded-2xl" style={{ background: `${v.tone}15`, color: v.tone }}>
                    <Icon size={28} weight="duotone" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Impact stats */}
      <section className="bg-forest py-16 text-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 sm:px-6 lg:grid-cols-4 lg:px-8">
          {STATS.map(([big, small]) => (
            <div key={small} className="text-center">
              <div className="font-display text-4xl font-extrabold text-gold sm:text-5xl">{big}</div>
              <div className="mt-1 text-sm text-white/75">{small}</div>
            </div>
          ))}
        </div>
      </section>

      {/* What we offer */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-terracotta">La plateforme</span>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Tout pour réussir, au même endroit</h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {OFFER.map((o) => {
              const Icon = o.icon;
              return (
                <div key={o.title} className="flex gap-4 rounded-3xl border border-border bg-white p-6 shadow-sm">
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-terracotta/10 text-terracotta">
                    <Icon size={24} weight="duotone" />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-bold text-forest">{o.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{o.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="bg-cream py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 rounded-[2rem] border border-border bg-white p-8 shadow-sm sm:p-12 lg:grid-cols-[auto_1fr]">
            <div className="flex flex-col items-center text-center">
              <div className="relative">
                {founderOk ? (
                  <img src={FOUNDER_PHOTO} alt="M. Eloungou Etienne Landry" onError={() => setFounderOk(false)} className="h-64 w-48 rounded-3xl border-4 border-white object-cover object-top shadow-xl" />
                ) : (
                  <div className="grid h-64 w-48 place-items-center rounded-3xl bg-gradient-to-br from-forest to-[#05357E] font-display text-5xl font-extrabold text-white shadow-xl">EL</div>
                )}
                <img src={MASCOT.point} alt="" className="absolute -bottom-4 -right-6 w-16 drop-shadow-lg" />
              </div>
              <div className="mt-5 font-display text-lg font-extrabold text-forest">M. Eloungou Etienne Landry</div>
              <div className="text-sm font-semibold text-terracotta">Fondateur du groupe YAYA · « M. YAYA »</div>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-terracotta">L'homme derrière YAYA</span>
              <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Une vision au service de la jeunesse</h2>
              <p className="mt-4 leading-relaxed text-slate-600">
                Passionné d'éducation et profondément attaché à la réussite de la jeunesse camerounaise, M. Eloungou
                Etienne Landry a fait de l'accès au savoir le combat d'une vie. Avec le groupe YAYA, il rassemble
                enseignants, pédagogues et technologies pour donner à chaque élève un accompagnement digne des meilleurs,
                quel que soit son point de départ.
              </p>
              <blockquote className="mt-5 border-l-4 border-terracotta pl-4">
                <p className="italic leading-relaxed text-forest">
                  « Mettre un professeur et un coach dans chaque poche — même hors connexion — pour que la réussite ne
                  dépende plus jamais du lieu où l'on naît. »
                </p>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-terracotta px-8 py-16 text-center text-white">
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
          <div className="absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-white/10" />
          <div className="relative">
            <img src={MASCOT.wave} alt="" className="mx-auto w-28 drop-shadow-xl" />
            <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-extrabold sm:text-4xl">Rejoignez l'aventure YAYA</h2>
            <p className="mx-auto mt-4 max-w-xl text-white/90">Des milliers d'élèves progressent déjà chaque jour. Et si c'était votre tour ?</p>
            <Link to="/connexion?mode=register" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-bold text-terracotta transition hover:-translate-y-0.5 hover:bg-cream">
              Créer mon compte gratuit <ArrowRight weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
